import { useEffect, useMemo, useState } from "react";
import { GridLoader } from "react-spinners";

import { Composer, Container, MessageList, useWebchat } from "@botpress/webchat";
import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";

const BotpressInteractiveAssessmentDialog = props => {
  const {
    open,
    onClose,
    onAccept,
    singleConversation,
    newestConversation,
    oldestConversation,
    analyticsBotODM,
    dialogTitle = "A.I. Analysis",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "md",
    fullWidth = true,
  } = props;

  const theme = useTheme();
  const [botData] = useState(analyticsBotODM?.omniData.botData);

  const onAcceptHandler = () => {
    onAccept();
  };

  const onCloseHandler = () => {
    onClose();
  };

  //#region Botpress Assessment Webchat Setup

  const webchatClientID = analyticsBotODM?.omniData.webchatClientId;
  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const { client, on, messages, isTyping, user, clientState, newConversation } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here
    feedbackEnabled: false,
    storageKey: `botpress-botpressInteractiveAssessmentDialog-${webchatClientID}`,
    storageLocation: "localStorage",
  });

  const enrichedMessages = useMemo(
    () =>
      messages.map(message => {
        const { authorId } = message;
        const direction = authorId === user?.userId ? "outgoing" : "incoming";
        return {
          ...message,
          direction,
          sender:
            direction === "outgoing"
              ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
              : { name: botData?.botName ?? "Bot", avatar: botData?.botAvatar },
        };
      }),
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [botData?.botName, messages, user?.userId, user?.name, user?.pictureUrl],
  );

  const [userInitialized, SetUserInitialized] = useState(false);

  useEffect(() => {
    const unsubscribe = on("conversation", event => {
      client?.sendMessage({
        type: "text",
        text: "Please analyze the conversation and provide insights.",
      });
    });

    return () => {
      unsubscribe();
    };
  }, [on, client]);

  const [countdown, setCountdown] = useState(-1);

  useEffect(() => {
    //ok start the automated process
    if (countdown > 0) {
      const countdownFunction = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 500);
      return () => clearTimeout(countdownFunction);
    } else if (countdown === 0) {
      newConversation().then(() => {
        setIsWebchatOpen(true);
      });
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  useEffect(() => {
    if (client && singleConversation && !userInitialized) {
      try {
        client.updateUser({
          data: { singleConversation: JSON.stringify(singleConversation) },
        });
        SetUserInitialized(true);
        setCountdown(1);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, singleConversation, userInitialized]);

  useEffect(() => {
    if (client && newestConversation && oldestConversation && !userInitialized) {
      try {
        client.updateUser({
          data: {
            newestConversation: JSON.stringify(newestConversation),
            oldestConversation: JSON.stringify(oldestConversation),
          },
        });
        SetUserInitialized(true);
        setCountdown(1);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, newestConversation, oldestConversation, userInitialized]);

  //#endregion

  return (
    <Dialog
      open={open}
      onClose={() => onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent sx={{ padding: 0, overflowX: "hidden" }}>
        {!isWebchatOpen && (
          <Box
            sx={{
              minHeight: "75vh",
              maxHeight: "75vh",
              width: "100%",
              alignContent: "center",
              textAlign: "center",
            }}
          >
            <GridLoader size={56} color={theme.palette.primary.main} />
          </Box>
        )}
        {isWebchatOpen && (
          <Container
            connected={clientState !== "disconnected"}
            style={{
              minHeight: "75vh",
              maxHeight: "75vh",
              width: "100%",
              display: isWebchatOpen ? "flex" : "none",
              borderRadius: 0,
              border: "none",
            }}
          >
            <MessageList
              botAvatar={<AddIcon />}
              // botAvatar={botData?.botAvatar}
              botName={"Assessment Bot"}
              botDescription={"Helping you analyze the conversation"}
              isTyping={isTyping}
              headerMessage="Beginning Analysis"
              showMarquee={true}
              messages={enrichedMessages}
              sendMessage={client?.sendMessage}
            />
            <Composer
              disableComposer={false}
              isReadOnly={false}
              allowFileUpload={true}
              connected={clientState !== "disconnected"}
              sendMessage={client?.sendMessage}
              uploadFile={client?.uploadFile}
              composerPlaceholder="Type a message..."
            />
          </Container>
        )}
      </DialogContent>
      <DialogActions>
        {cancelMessage && (
          <Button onClick={onCloseHandler} color="secondary" variant="contained">
            {cancelMessage}
          </Button>
        )}
        <Button onClick={onAcceptHandler} color="secondary" variant="contained">
          {okMessage}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BotpressInteractiveAssessmentDialog;
